import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface SidebarStepMetricsContextValue {
  /** Live rack list length (session / subsection); draft also via formValues. */
  rackUnitCount: number | undefined;
  setRackUnitCount: (count: number | undefined) => void;
  /** Live Large UPS + Power Distribution count during Request Detail edit. */
  facilityPowerUnitCount: number | undefined;
  setFacilityPowerUnitCount: (count: number | undefined) => void;
  /**
   * Total Supporting Documents attachments on the open request (#1534).
   *
   * Pushed by `RequestDetail` from the very map it hands the step, rather than
   * fetched again here: `useAttachments` is plain state + effect, not React
   * Query, so a second caller would mean a second HTTP fetch per sidebar mount,
   * a count that goes stale the moment the screen refetches after an
   * upload/delete, and a re-run at the concurrent-fetch storm behind QA-52.
   */
  supportingDocumentCount: number | undefined;
  setSupportingDocumentCount: (count: number | undefined) => void;
  /** Merged request or create form values for sidebar `N of M` subtitles. */
  formValues: Record<string, unknown> | null;
  setFormValues: (values: Record<string, unknown> | null) => void;
  /** Mirrors step `showFullDetails` for permission-aware fill counts. */
  showFullDetails: boolean | undefined;
  setShowFullDetails: (value: boolean | undefined) => void;
  requestStage: string | undefined;
  setRequestStage: (stage: string | undefined) => void;
  requestId: string | undefined;
  setRequestId: (requestId: string | undefined) => void;
}

/** Shallow snapshot compare — avoids context churn when screens spread new object refs. */
export function areSidebarMetricsFormValuesEqual(
  a: Record<string, unknown> | null,
  b: Record<string, unknown> | null,
): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every(key => Object.is(a[key], b[key]));
}

function setStateIfChanged<T>(
  current: T,
  next: T,
  areEqual: (left: T, right: T) => boolean = Object.is,
): T {
  return areEqual(current, next) ? current : next;
}

const SidebarStepMetricsContext = createContext<SidebarStepMetricsContextValue>(
  {
    rackUnitCount: undefined,
    setRackUnitCount: () => {},
    facilityPowerUnitCount: undefined,
    setFacilityPowerUnitCount: () => {},
    supportingDocumentCount: undefined,
    setSupportingDocumentCount: () => {},
    formValues: null,
    setFormValues: () => {},
    showFullDetails: undefined,
    setShowFullDetails: () => {},
    requestStage: undefined,
    setRequestStage: () => {},
    requestId: undefined,
    setRequestId: () => {},
  },
);

export const SidebarStepMetricsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [rackUnitCount, setRackUnitCountState] = useState<number | undefined>(
    undefined,
  );
  const [facilityPowerUnitCount, setFacilityPowerUnitCountState] = useState<
    number | undefined
  >(undefined);
  const [supportingDocumentCount, setSupportingDocumentCountState] = useState<
    number | undefined
  >(undefined);
  const [formValues, setFormValuesState] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [showFullDetails, setShowFullDetailsState] = useState<
    boolean | undefined
  >(undefined);
  const [requestStage, setRequestStageState] = useState<string | undefined>(
    undefined,
  );
  const [requestId, setRequestIdState] = useState<string | undefined>(
    undefined,
  );

  const setRackUnitCount = useCallback((count: number | undefined) => {
    setRackUnitCountState(current =>
      setStateIfChanged(current, count, Object.is),
    );
  }, []);
  const setFacilityPowerUnitCount = useCallback((count: number | undefined) => {
    setFacilityPowerUnitCountState(current =>
      setStateIfChanged(current, count, Object.is),
    );
  }, []);
  const setSupportingDocumentCount = useCallback(
    (count: number | undefined) => {
      setSupportingDocumentCountState(current =>
        setStateIfChanged(current, count, Object.is),
      );
    },
    [],
  );
  const setFormValues = useCallback(
    (values: Record<string, unknown> | null) => {
      setFormValuesState(current =>
        setStateIfChanged(current, values, areSidebarMetricsFormValuesEqual),
      );
    },
    [],
  );
  const setShowFullDetails = useCallback((value: boolean | undefined) => {
    setShowFullDetailsState(current =>
      setStateIfChanged(current, value, Object.is),
    );
  }, []);
  const setRequestStage = useCallback((stage: string | undefined) => {
    setRequestStageState(current =>
      setStateIfChanged(current, stage, Object.is),
    );
  }, []);
  const setRequestId = useCallback((id: string | undefined) => {
    setRequestIdState(current => setStateIfChanged(current, id, Object.is));
  }, []);

  const value = useMemo(
    () => ({
      rackUnitCount,
      setRackUnitCount,
      facilityPowerUnitCount,
      setFacilityPowerUnitCount,
      supportingDocumentCount,
      setSupportingDocumentCount,
      formValues,
      setFormValues,
      showFullDetails,
      setShowFullDetails,
      requestStage,
      setRequestStage,
      requestId,
      setRequestId,
    }),
    [
      rackUnitCount,
      setRackUnitCount,
      facilityPowerUnitCount,
      setFacilityPowerUnitCount,
      supportingDocumentCount,
      setSupportingDocumentCount,
      formValues,
      setFormValues,
      showFullDetails,
      setShowFullDetails,
      requestStage,
      setRequestStage,
      requestId,
      setRequestId,
    ],
  );

  return (
    <SidebarStepMetricsContext.Provider value={value}>
      {children}
    </SidebarStepMetricsContext.Provider>
  );
};

export function useSidebarStepMetrics(): SidebarStepMetricsContextValue {
  return useContext(SidebarStepMetricsContext);
}
